import { Injectable } from '@angular/core';
import { delay, finalize, isObservable, Observable } from 'rxjs';
import { Console, isDefined, isPromise } from '../../functions';
import { takeOnce } from '../../operators';
import { TAsyncFnOptions, TAsyncFnWrapper } from './async-fn.interface';

@Injectable({
  providedIn: 'root'
})
export class AsyncFnService {
  constructor() {}

  /**
   * @description
   * a function which executes an async function. the async function received as arg #1
   * is called and its progress is tracked to detect its finalization. this is useful to
   * indicate back to the consumer the state of the async process (pending or not).
   * @param asyncFn a function with async return value (either observable or promise)
   * @param pendingInd a reference to writable boolean signal
   */
  runAsyncMethod(asyncFn: TAsyncFnWrapper | undefined, asyncOptions: TAsyncFnOptions) {
    /** execute the async callback function, and get its async return object (observable | promise) */
    const fnResponse = asyncFn();

    /** check if the async callback function is an observable */
    if (isObservable(fnResponse)) {
      this.setPending(asyncOptions, true);
      Console.log(`Before Button clicked..`);
      (<Observable<unknown>>fnResponse)
        .pipe(
          takeOnce(),
          delay(asyncOptions?.trailTime || 0),
          /**
           * add finalize operator as last change event of the the async observable. this way -
           * pending state can always be reset to false when observable is complete */
          finalize(() => {
            this.setPending(asyncOptions, false);
            Console.log(`After Form submitted!`);
          })
        )
        .subscribe();
      return;
    }

    /** check if the async callback function is a promise */
    if (isPromise(fnResponse)) {
      this.setPending(asyncOptions, true);
      Console.log(`Before Button clicked..`);
      /**
       * add finally function as last change event of the the promise. this way -
       * pending state can always be reset to false when the promise is done */
      (<Promise<unknown>>fnResponse).finally(() => {
        this.setPending(asyncOptions, false);
        Console.log(`After Button clicked!`);
      });
    }
  }

  private setPending(asyncOptions: TAsyncFnOptions, value: boolean) {
    const isPendingInd = isDefined(asyncOptions?.pendingInd);
    if (!isPendingInd) {
      return;
    }
    const { pendingInd } = asyncOptions;
    pendingInd.set(value);
  }
}

/***/
/* usage examples
 **/

// /** call a method which returns an observable */
// httpWithObservable(model: IOtpModel): TAsyncFnWrapper {
//   return (model) => this.service.sendSomething(model);
// }

// /** call a method which returns a promise */
// sendOtpViaHttp(): TAsyncFnWrapper {
//   return async (model) => {
//     return new Promise<boolean>((resolve, reject) => {
//       setTimeout(() => {
//         resolve(true);
//       }, 3000);
//     }).finally(() => {
//       Console.log(`Original finally`);
//     });
//   };
// }

//  /** implement on a submit button */
//   <submit-button
//   [disabled]="isPendingSubmit()"
//   gtmClickEvent="sendOtpViaSmsClickEvent"
//   [formName]="formFactory.config.name"
// >
//   המשך
// </submit-button>
